import { Link } from 'react-router-dom';
import { materialInfo } from '@/mocks/products';

// Expanded material details beyond what's in the mocks
const expandedMaterials = [
  {
    ...materialInfo[0],
    details: [
      'Sustainably tapped from FSC-certified rubber trees in Kerala, India — our natural rubber supports local farming communities while giving your dog a chew toy that actually lasts.',
      'Every batch is lab-tested for BPA, PVC, phthalates, and heavy metals. If it doesn\'t pass, it doesn\'t ship. Period.',
      'Unlike synthetic rubber and plastic toys, ours biodegrades at end-of-life without leaching microplastics into soil or water. Play hard, tread soft — for real.',
    ],
  },
  {
    ...materialInfo[1],
    details: [
      'Jute and hemp are nature\'s workhorses — they grow fast, need almost no irrigation, and require zero pesticides. That means a lower environmental footprint before the toy even exists.',
      'When your dog chews and sheds small fibres, those fibres are natural plant matter — not synthetic microplastics. They pass safely through the digestive system.',
      'We partner with women-led weaving collectives in West Bengal who hand-twist every rope and weave every collar. Fair wages, traditional craft, zero exploitative labour.',
    ],
  },
  {
    ...materialInfo[2],
    details: [
      'Coconut coir is the fibrous husk between the shell and the outer coconut — an abundant by-product of South Indian coconut farming that would otherwise be burned as agricultural waste.',
      'Bamboo is one of the fastest-regenerating plants on earth. Our bamboo is harvested at peak maturity (3-5 years) from managed groves in Northeast India, never from old-growth forest.',
      'Both materials require minimal processing — no chemical bleaching, no synthetic binders. Just cleaning, shaping, and compressing. What your pet chews is what nature made.',
    ],
  },
  {
    ...materialInfo[2],
    details: [
      'Coconut coir is the fibrous husk between the shell and the outer coconut — an abundant by-product of South Indian coconut farming that would otherwise be burned as agricultural waste.',
      'Bamboo is one of the fastest-regenerating plants on earth. Our bamboo is harvested at peak maturity (3-5 years) from managed groves in Northeast India, never from old-growth forest.',
      'Both materials require minimal processing — no chemical bleaching, no synthetic binders. Just cleaning, shaping, and compressing. What your pet chews is what nature made.',
    ],
  },
];

// Fix: Organic Cotton needs its own details
const organicCottonExpanded = {
  ...materialInfo[3],
  details: [
    'Our organic cotton is grown in rain-fed farms across central India — no synthetic pesticides, no GMO seeds, and significantly less water than conventional cotton. Better for the soil, the farmers, and your pet.',
    'Every cotton toy and bandana is hand-stitched by skilled artisans. The natural dyes we use are derived from plants and minerals — indigo, turmeric, madder root, and iron oxide — so the colours are as safe as the fabric itself.',
    'Machine-washable means these toys stay hygienic through slobber, dirt, and everything else your pet throws at them. They get softer with every wash, not weaker.',
  ],
};

const expandedMaterialList = [
  expandedMaterials[0],
  expandedMaterials[1],
  expandedMaterials[2],
  organicCottonExpanded,
];

export default function AboutPage() {
  return (
    <main className="bg-background-50">
      {/* Hero Section */}
      <section className="relative w-full h-[380px] md:h-[480px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20lifestyle%20scene%20of%20a%20happy%20dog%20and%20cat%20playing%20together%20on%20a%20soft%20cream%20rug%20in%20a%20bright%20airy%20modern%20Indian%20home%2C%20natural%20sunlight%20through%20windows%2C%20cozy%20aesthetic%2C%20editorial%20pet%20photography%2C%20warm%20tones%2C%20no%20synthetic%20toys%20visible&width=1600&height=600&seq=about-hero-2026&orientation=landscape"
          alt="A dog and cat playing together at home"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/45 via-foreground-950/30 to-foreground-950/50" />
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 h-full flex flex-col items-center justify-center text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-10 h-10 rounded-full bg-accent-500/20 flex items-center justify-center">
              <i className="ri-footprint-fill text-accent-500 text-xl"></i>
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 tracking-tight">
              About <span className="text-accent-500">Pounce</span>Bounce
            </h1>
          </div>
          <p className="text-background-50/80 text-sm md:text-lg max-w-xl leading-relaxed">
            Premium pet toys and accessories made from non-toxic, biodegradable materials — for dog and cat parents who treat their pets as family.
          </p>
        </div>
      </section>

      {/* Brand Story */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-500 uppercase tracking-widest mb-3">
              <i className="ri-footprint-line"></i> Our Story
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-4">
              Every <span className="text-primary-500">Pounce</span> and <span className="text-primary-500">Bounce</span> Has a Story
            </h2>
          </div>

          <div className="space-y-6 text-foreground-700 leading-relaxed text-sm md:text-base">
            <p>
              <strong className="text-foreground-900">PounceBounce was born from a simple frustration:</strong> walking into pet stores across India and finding aisle after aisle of bright plastic toys that smelled like chemicals, fell apart in a week, and came with zero information about what they were actually made of.
            </p>
            <p>
              The name says it all. <strong className="text-foreground-900">Pounce</strong> is for the cats — the stalkers, the pouncers, the silent hunters who deserve toys as elegant as their instincts. <strong className="text-foreground-900">Bounce</strong> is for the dogs — the fetchers, the chewers, the boundless-energy goofballs who need toys that can keep up.
            </p>
            <p>
              Together, <strong className="text-primary-500">PounceBounce</strong> stands for one thing: <em>toys and accessories you can trust without reading the fine print</em>. Because we already read it. We already tested it. We already threw out everything that didn't meet the standard.
            </p>
          </div>

          {/* Mission stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 mt-12">
            <div className="bg-secondary-100 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-shield-check-line text-primary-500 text-xl"></i>
              </div>
              <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-800">100%</div>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">Non-Toxic Guarantee</p>
            </div>
            <div className="bg-secondary-100 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-recycle-line text-primary-500 text-xl"></i>
              </div>
              <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-800">Zero</div>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">Synthetic Microplastics</p>
            </div>
            <div className="bg-secondary-100 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-flag-line text-primary-500 text-xl"></i>
              </div>
              <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-800">100%</div>
              <p className="text-xs md:text-sm text-foreground-500 mt-1">Made in India</p>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Promise Banner */}
      <section className="w-full bg-primary-500 py-14 md:py-18 px-4 md:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <i className="ri-double-quotes-l text-accent-500 text-2xl opacity-60"></i>
          </div>
          <blockquote className="font-heading text-xl md:text-2xl font-bold text-background-50 leading-relaxed mb-4">
            &ldquo;We believe that what your pet chews, cuddles, and carries every day should be as safe as the food you put in their bowl. No compromises, no greenwashing, no &lsquo;good enough.&rsquo;&rdquo;
          </blockquote>
          <p className="text-primary-200 text-sm md:text-base">&mdash; Priya &amp; Arjun, Co-Founders</p>
        </div>
      </section>

      {/* Our Material Promise — expanded */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-500 uppercase tracking-widest mb-3">
              <i className="ri-leaf-line"></i> Our Material Promise
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-4">
              Every Material Has a Story. We Tell It Honestly.
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
              No vague &ldquo;eco-friendly&rdquo; claims. No hidden synthetics. Here&rsquo;s exactly what goes into every PounceBounce product — and why.
            </p>
          </div>

          <div className="space-y-6">
            {expandedMaterialList.map((mat, i) => (
              <div
                key={i}
                className={`rounded-2xl p-6 md:p-8 ${
                  i % 2 === 0 ? 'bg-secondary-50' : 'bg-background-100/60'
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-5 md:gap-8">
                  {/* Icon & Name */}
                  <div className="flex items-center gap-4 md:w-60 flex-shrink-0">
                    <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${mat.icon} text-primary-500 text-2xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-foreground-800">{mat.name}</h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{mat.description.length > 80 ? mat.description.slice(0, 80) + '...' : mat.description}</p>
                    </div>
                  </div>

                  {/* Expanded details */}
                  <div className="flex-1 space-y-3">
                    {mat.details.map((detail, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-footprint-line text-accent-500 text-xs"></i>
                        </span>
                        <p className="text-sm text-foreground-700 leading-relaxed">{detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability Commitment */}
      <section className="w-full bg-primary-500 px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            {/* Left: Icon block */}
            <div className="flex-shrink-0">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-primary-600 flex items-center justify-center">
                <i className="ri-earth-line text-4xl md:text-5xl text-accent-500"></i>
              </div>
            </div>

            {/* Right: Content */}
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-500 uppercase tracking-widest mb-3">
                <i className="ri-leaf-line"></i> Our Commitment
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
                Sustainability Is Not a Buzzword Here
              </h2>
              <div className="space-y-3 text-primary-200 text-sm md:text-base leading-relaxed">
                <p>
                  Every PounceBounce product starts with a question: <em className="text-background-50">&ldquo;Where does this material come from, and where does it go when it&rsquo;s done?&rdquo;</em> If we can&rsquo;t answer both with confidence, we don&rsquo;t make it.
                </p>
                <p>
                  Our natural rubber comes from FSC-certified plantations. Our coconut coir is an agricultural by-product that would otherwise be burned. Our packaging is plastic-free and home-compostable. Our shipping partners are chosen for carbon-conscious logistics.
                </p>
                <p className="text-background-50 font-medium">
                  We&rsquo;re not perfect — but we&rsquo;re transparent. And we&rsquo;re improving every season.
                </p>
              </div>

              {/* Small commitment grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
                <div className="flex items-center gap-2 bg-primary-600/50 rounded-lg px-4 py-3">
                  <i className="ri-plant-line text-accent-500"></i>
                  <span className="text-xs text-background-50">Plastic-Free Packaging</span>
                </div>
                <div className="flex items-center gap-2 bg-primary-600/50 rounded-lg px-4 py-3">
                  <i className="ri-user-heart-line text-accent-500"></i>
                  <span className="text-xs text-background-50">Fair-Wage Partnerships</span>
                </div>
                <div className="flex items-center gap-2 bg-primary-600/50 rounded-lg px-4 py-3">
                  <i className="ri-drop-line text-accent-500"></i>
                  <span className="text-xs text-background-50">Carbon-Conscious Shipping</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-5">
            <i className="ri-heart-line text-accent-500 text-2xl"></i>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-4">
            From Our Pack to Yours
          </h2>
          <p className="text-foreground-500 text-sm md:text-base mb-8 max-w-lg mx-auto">
            Every toy, collar, and bandana we make is something we&rsquo;d give to our own pets first. Because that&rsquo;s the only standard that matters.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Browse the Collection
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 border border-background-300 text-foreground-600 rounded-full font-medium text-sm hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line"></i>
              Say Hello on WhatsApp
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}